/**
 * CommissionSettings — "نسبة عمولة تابي وتمارا" screen.
 *
 * Each provider (Tabby / Tamara) has:
 *   • fee_type: "percentage" | "fixed" — radio picker
 *   • fee_percent (0..100) if type=percentage
 *   • fee_fixed_amount (AED, >= 0) if type=fixed
 *   • fee_enabled toggle → when off, no markup is applied
 *
 * Editing is BLOCKED while fee_enabled=true. The operator must disable
 * commission first, tweak the value/type, then re-enable.
 *
 * Reads/writes: /api/admin/payment-methods
 *   POST /{id}/set-fee      { fee_type, fee_percent, fee_fixed_amount }
 *   POST /{id}/toggle-fee   → flips fee_enabled
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save, Loader2, RefreshCw, Info, ToggleLeft, ToggleRight, Lock,
} from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getAuthHeaders } from '../../utils/authHeaders';
import { floorAED } from '../../utils/money';
import { DirhamPrice } from '../../components/DirhamPrice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMMISSION_PROVIDERS = ['tabby', 'tamara'];

const emptyDraft = () => ({ fee_type: 'percentage', fee_percent: '0', fee_fixed_amount: '0' });

export default function CommissionSettings() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/payment-methods`, { headers: getAuthHeaders() });
      const filtered = (r.data.data || []).filter(m => COMMISSION_PROVIDERS.includes(m.provider));
      setMethods(filtered);
    } catch {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const init = {};
    methods.forEach(m => {
      init[m.id] = {
        fee_type: m.fee_type || 'percentage',
        fee_percent: String(m.fee_percent ?? 0),
        fee_fixed_amount: String(m.fee_fixed_amount ?? 0),
      };
    });
    setDrafts(init);
  }, [methods]);

  const saveFee = async (m) => {
    const d = drafts[m.id] || emptyDraft();
    const type = d.fee_type;
    const pct = parseFloat(d.fee_percent || 0);
    const fixed = parseFloat(d.fee_fixed_amount || 0);
    if (type === 'percentage' && (isNaN(pct) || pct < 0 || pct > 100)) {
      toast.error(t('النسبة يجب أن تكون بين 0 و 100', 'Percentage must be 0–100'));
      return;
    }
    if (type === 'fixed' && (isNaN(fixed) || fixed < 0)) {
      toast.error(t('المبلغ الثابت يجب أن يكون رقم موجب', 'Fixed amount must be a positive number'));
      return;
    }
    setSavingId(m.id);
    try {
      await axios.post(
        `${API_URL}/api/admin/payment-methods/${m.id}/set-fee`,
        { fee_type: type, fee_percent: pct, fee_fixed_amount: fixed },
        { headers: getAuthHeaders() },
      );
      toast.success(t('✓ تم حفظ العمولة', '✓ Commission saved'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSavingId(null);
    }
  };

  const toggleFee = async (m) => {
    setTogglingId(m.id);
    try {
      await axios.post(
        `${API_URL}/api/admin/payment-methods/${m.id}/toggle-fee`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم تحديث حالة التفعيل', 'Enabled state updated'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحديث', 'Update failed'));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance/settings" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'إعدادات القسم المالي', label_en: 'Finance Settings', path: '/dashboard/finance/settings' },
            { label_ar: 'عمولة تابي وتمارا', label_en: 'Tabby & Tamara Commission', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className={isAr ? 'text-right' : 'text-left'}>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {t('عمولة تابي وتمارا', 'Tabby & Tamara Commission')}
            </h1>
            <p className="text-white/50 text-sm max-w-2xl">
              {t('اختر إما نسبة عمولة أو مبلغ ثابت لكل مزود، ثم فعّل التطبيق. عند التفعيل — القيمة تُضاف تلقائياً على مبلغ رابط الدفع.',
                 'Choose either a percentage OR a fixed amount per provider, then enable it. When enabled — the value is auto-added to any payment link.')}
            </p>
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
            data-testid="commission-refresh"
          >
            <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {loading ? (
          <div className="p-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 text-amber-200/80 text-xs flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                {t('يجب إيقاف التفعيل أولاً لتعديل القيم. اختر بين "نسبة %" أو "مبلغ ثابت" — واحدة فقط في المرة.',
                   'You must disable the commission first to edit the values. Pick EITHER "Percentage %" OR "Fixed Amount" — one at a time.')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {methods.map((m) => {
                const d = drafts[m.id] || emptyDraft();
                const isPct = d.fee_type === 'percentage';
                const feeEnabled = !!m.fee_enabled;
                const readOnly = feeEnabled;

                // Preview on 1,000 AED base
                const preview = isPct
                  ? 1000 + 1000 * (parseFloat(d.fee_percent || 0) / 100)
                  : 1000 + parseFloat(d.fee_fixed_amount || 0);

                const isDirty =
                  d.fee_type !== (m.fee_type || 'percentage')
                  || String(d.fee_percent) !== String(m.fee_percent ?? 0)
                  || String(d.fee_fixed_amount) !== String(m.fee_fixed_amount ?? 0);

                const setDraft = (patch) =>
                  setDrafts(state => ({ ...state, [m.id]: { ...(state[m.id] || emptyDraft()), ...patch } }));

                return (
                  <div key={m.id}
                       className={`bg-white/[0.02] border rounded-2xl p-5 transition-colors ${
                         feeEnabled ? 'border-emerald-500/20' : 'border-white/10'
                       }`}
                       data-testid={`commission-card-${m.provider}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-white/[0.02]"
                           style={{ border: `1px solid ${m.color}30` }}>
                        {m.logo_url ? (
                          <img src={m.logo_url} alt="" className="w-full h-full object-contain p-1" />
                        ) : (
                          <span style={{ color: m.color }} className="font-bold">{m.display_name?.[0]}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold">{isAr ? m.display_name_ar : m.display_name}</h3>
                        <p className="text-white/40 text-xs">{isAr ? m.description_ar : m.description_en}</p>
                      </div>
                    </div>

                    {/* Enable / Disable */}
                    <div className={`flex items-center justify-between gap-3 p-3 mb-4 rounded-xl border ${
                      feeEnabled
                        ? 'bg-emerald-500/5 border-emerald-500/20'
                        : 'bg-white/[0.02] border-white/10'
                    }`}>
                      <div>
                        <div className="text-sm text-white font-medium">
                          {t('حالة تطبيق العمولة', 'Commission application')}
                        </div>
                        <div className={`text-[11px] ${feeEnabled ? 'text-emerald-400' : 'text-white/40'}`}>
                          {feeEnabled ? t('مُفعّل — الحقول مغلقة للتعديل', 'Enabled — fields locked for editing')
                                     : t('مُعطّل — يمكنك تعديل القيم', 'Disabled — you may edit the values')}
                        </div>
                      </div>
                      <button onClick={() => toggleFee(m)} disabled={togglingId === m.id}
                              className="p-1.5 rounded-md hover:bg-white/5 disabled:opacity-50"
                              data-testid={`commission-toggle-${m.provider}`}>
                        {togglingId === m.id ? (
                          <Loader2 className="w-6 h-6 animate-spin text-white/40" />
                        ) : feeEnabled ? (
                          <ToggleRight className="w-9 h-9 text-emerald-400" />
                        ) : (
                          <ToggleLeft className="w-9 h-9 text-white/30" />
                        )}
                      </button>
                    </div>

                    {/* Type radio */}
                    <label className="block text-white/70 text-xs mb-2">
                      {t('نوع العمولة', 'Commission type')}
                    </label>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {[
                        { id: 'percentage', ar: 'نسبة %', en: 'Percentage %' },
                        { id: 'fixed',      ar: 'مبلغ ثابت', en: 'Fixed Amount' },
                      ].map((opt) => {
                        const active = d.fee_type === opt.id;
                        return (
                          <button
                            key={opt.id}
                            onClick={() => !readOnly && setDraft({ fee_type: opt.id })}
                            disabled={readOnly}
                            className={`px-3 py-2.5 rounded-lg border text-sm transition-colors text-start ${
                              active
                                ? 'bg-[#d4af37]/10 border-[#d4af37] text-white font-medium'
                                : 'bg-[#050505] border-white/10 text-white/70 hover:border-white/30'
                            } disabled:opacity-60 disabled:cursor-not-allowed`}
                            data-testid={`commission-type-${m.provider}-${opt.id}`}
                          >
                            <div className="flex items-center gap-2">
                              {active && <span className="w-2 h-2 rounded-full bg-[#d4af37]" />}
                              <span>{isAr ? opt.ar : opt.en}</span>
                              {readOnly && active && <Lock className="w-3 h-3 ms-auto text-white/40" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Values */}
                    {isPct ? (
                      <>
                        <label className="block text-white/70 text-xs mb-1.5">
                          {t('نسبة العمولة %', 'Commission %')}
                        </label>
                        <div className="relative flex-1">
                          <Input
                            type="number" step="0.01" min="0" max="100"
                            value={d.fee_percent}
                            disabled={readOnly}
                            onChange={(e) => setDraft({ fee_percent: e.target.value })}
                            className="bg-[#050505] border-white/10 text-white h-11 pe-8 disabled:opacity-60"
                            dir="ltr"
                            data-testid={`commission-input-percent-${m.provider}`}
                          />
                          <span className="absolute inset-y-0 end-3 flex items-center text-white/40 text-sm">%</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <label className="block text-white/70 text-xs mb-1.5">
                          {t('المبلغ الثابت', 'Fixed Amount')}
                        </label>
                        <div className="relative flex-1">
                          <Input
                            type="number" step="0.01" min="0"
                            value={d.fee_fixed_amount}
                            disabled={readOnly}
                            onChange={(e) => setDraft({ fee_fixed_amount: e.target.value })}
                            className="bg-[#050505] border-white/10 text-white h-11 pe-12 disabled:opacity-60"
                            dir="ltr"
                            data-testid={`commission-input-fixed-${m.provider}`}
                          />
                          <img src="/currency/dirham-white.png" alt="AED" className="absolute inset-y-0 end-3 flex items-center text-white/40 text-xs h-3.5 w-3.5 object-contain opacity-80" />
                        </div>
                      </>
                    )}

                    {/* Save */}
                    <div className="mt-3">
                      <Button
                        onClick={() => saveFee(m)}
                        disabled={!isDirty || savingId === m.id || readOnly}
                        className="w-full bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold h-11 disabled:opacity-50"
                        data-testid={`commission-save-${m.provider}`}
                      >
                        {savingId === m.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : readOnly
                            ? <><Lock className="w-4 h-4 me-1" />{t('عطّل التفعيل أولاً للتعديل', 'Disable first to edit')}</>
                            : <><Save className="w-4 h-4 me-1" />{t('حفظ العمولة', 'Save commission')}</>}
                      </Button>
                    </div>

                    {/* Preview */}
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <div className="text-[11px] text-white/40 mb-2">
                        {t('معاينة: على 1,000 درهم', 'Preview: on 1,000 AED')}
                        {!feeEnabled && (
                          <span className="ms-2 text-amber-400">
                            {t('(مُعطّل — لن تُطبَّق)', '(disabled — no markup)')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-white/60">1,000</span>
                        <span className="text-white/40">+</span>
                        <span className={feeEnabled ? 'text-amber-400' : 'text-white/30 line-through'}>
                          {floorAED(preview - 1000).toFixed(2)}
                        </span>
                        <span className="text-white/40">=</span>
                        <span className={`font-bold ${feeEnabled ? 'text-emerald-400' : 'text-white/60'}`}>
                          <DirhamPrice value={(feeEnabled ? preview : 1000)} decimals={2} size="sm" />
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
